import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Grid } from "@mui/material";
import type { SxProps } from "@mui/material";
import type { User } from "@sdeapps/react-core";
import CommandeCardSkeleton from "components/molecules/cards/CommandeCard/CommandeCardSkeleton";
import type Commande from "models/Commande";
import LazyLoader from "utils/LazyLoader";
import CommandeListe from "./CommandeListe";

interface LazyLoadedSearchResultsProps {
  sx?: SxProps;
  initialResultsNumber?: number;
  step?: number;

  commandes: Array<Commande>;
  agents?: Array<User>;
  uri?: (id: string) => string;
  displayArticleListHeader?: boolean;
  displayCommentaires?: boolean;
  hasCommandeLink?: boolean;
}

const DEFAULT_NUMBER_OF_RESULTS_TO_DISPLAY = 30;
const INCREASE_STEP_NUMBER_OF_RESULTS_TO_DISPLAY = 20;

/**
 * Affiche l'interface (messages, CircularProgress) et les résultats de la recherche.
 * A entourer d'un SearchProvider.
 * @param sx?: SxProps
 */
function LazyLoadedCommandeList({
  sx,
  initialResultsNumber = DEFAULT_NUMBER_OF_RESULTS_TO_DISPLAY,
  step = INCREASE_STEP_NUMBER_OF_RESULTS_TO_DISPLAY,

  commandes,
  agents,
  uri,
  displayArticleListHeader = false,
  displayCommentaires = false,
  hasCommandeLink = false,
}: Readonly<LazyLoadedSearchResultsProps>): ReactNode {
  const [numberOfResultsToDisplay, setNumberOfResultsToDisplay] = useState(initialResultsNumber);

  useEffect(() => {
    setNumberOfResultsToDisplay(initialResultsNumber);
  }, [commandes, initialResultsNumber]);

  function increaseNumberOfResultsToDisplay(): void {
    const filteredLength: number = commandes.length;
    if (numberOfResultsToDisplay === commandes.length) {
      return;
    }

    const newNumberOfResultsToDisplay: number = Math.min(
      numberOfResultsToDisplay + step,
      filteredLength
    );

    setNumberOfResultsToDisplay(newNumberOfResultsToDisplay);
  }

  return (
    <Grid container spacing={1} sx={{ position: "relative", ...sx }} size={12}>
      <CommandeListe
        commandes={commandes.slice(0, numberOfResultsToDisplay)}
        agents={agents}
        hasCommandeLink={hasCommandeLink}
        uri={uri}
        displayArticleListHeader={displayArticleListHeader}
        displayCommentaires={displayCommentaires}
      />
      <LazyLoader lazyLoad={increaseNumberOfResultsToDisplay} />
      <Grid container spacing={4} size={12}>
        {commandes.length > numberOfResultsToDisplay &&
          [1, 2, 3, 4].map((k) => (
            <Grid key={k} size={{ xs: 12, lg: 6 }}>
              <CommandeCardSkeleton />
            </Grid>
          ))}
      </Grid>
      <LazyLoader lazyLoad={increaseNumberOfResultsToDisplay} sx={{ bottom: "10vh" }} />
    </Grid>
  );
}

export default LazyLoadedCommandeList;
